import type { GenerationPhase } from './generation_stream'

export const GENERATION_STAGES = [
  { key: 'starting', label: 'Getting started' },
  { key: 'writing', label: 'Writing the assessment' },
  { key: 'checking', label: 'Checking it over' },
  { key: 'images', label: 'Creating images' },
  { key: 'resources', label: 'Creating printable resources' },
  { key: 'saving', label: 'Saving it to this page' }
] as const

export type GenerationStageKey = typeof GENERATION_STAGES[number]['key']

// Allowlists throughout: an event type, tool name or count added upstream fails closed to
// "not displayed", and what renders is always our own copy, never a string from the stream.
// content_chunk carries the model's own prose, so it is mapped for its arrival only.
const STAGE_BY_PHASE: Record<string, GenerationStageKey> = {
  content_chunk: 'writing',
  content_block_end: 'writing',
  turn_complete: 'writing',
  validation_feedback: 'checking',
  asset_generation_start: 'images',
  // Not 'saving': stages only move forward, so that would strand "Saving it to this page"
  // over the whole printable resources phase that follows.
  asset_generation_complete: 'images',
  supplemental_generation_start: 'resources',
  supplemental_generation_progress: 'resources',
  supplemental_generation_complete: 'saving'
}

/** Carries a detail line for what is running now, and moves no stage. */
export const DETAIL_PHASE_TYPE = 'tool_phase'

/**
 * Ends a tool's detail line. There is no tool-completion event, and a turn's tool_phase
 * events all precede its turn_complete, so without this the line names the last tool that
 * started long after it has returned.
 */
export const DETAIL_ENDING_PHASE_TYPE = 'turn_complete'

const TOOL_DETAIL_COPY: Record<string, string> = {
  list_assessment_schemas: 'Determining the type of assessment',
  get_assessment_schema: 'Determining the type of assessment',
  select_assessment_type: 'Determining the type of assessment',
  math_engine: 'Running calculations',
  validate_assessment: 'Reviewing the assessment',
  // reject_revision is the agent declining to revise, so "Revising" would say the opposite.
  reject_revision: 'Reviewing your feedback'
}

function ownValue<T>(record: Record<string, T>, key: unknown): T | null {
  if (typeof key !== 'string' || !Object.hasOwn(record, key)) return null
  return record[key]
}

/** A whole positive number off the wire; null, '' or true must not render as a count. */
function isCount(value: unknown): value is number {
  return typeof value === 'number' && Number.isInteger(value) && value > 0
}

const COUNT_DETAIL_COPY: Record<string, (data: Record<string, unknown>) => string | null> = {
  // The total only: images are made in one batch call, so nothing says which is in flight.
  asset_generation_start({ requested }) {
    if (!isCount(requested)) return null
    return requested === 1 ? '1 image to create' : `${requested} images to create`
  },
  // The server's index is zero-based, and it is a position rather than a progress count:
  // skipped resources leave gaps ("1 of 3", then "3 of 3"). Product kept the position on
  // 2026-09-03 rather than invent an ordinal the wire never sent.
  supplemental_generation_progress({ index, total }) {
    if (!isCount(total) || typeof index !== 'number' || !Number.isInteger(index)) return null
    if (index < 0 || index >= total) return null
    return `Creating resource ${index + 1} of ${total}`
  }
}

/** The stage a progress event moves the run to, or null if it moves nothing. */
export function stageForPhase(phaseType: string): GenerationStageKey | null {
  return ownValue(STAGE_BY_PHASE, phaseType)
}

export function stageOrder(stage: GenerationStageKey): number {
  return GENERATION_STAGES.findIndex((candidate) => candidate.key === stage)
}

/** The detail copy for a tool_phase event, or null so a tool with no copy clears the line. */
export function detailForPhase(phase: GenerationPhase): string | null {
  return ownValue(TOOL_DETAIL_COPY, phase.data.tool_name)
}

/** Whether an event carries a count for its stage; one whose count is unreadable clears the line. */
export function isCountPhase(phaseType: string): boolean {
  return ownValue(COUNT_DETAIL_COPY, phaseType) !== null
}

/** The detail copy for a count-carrying event, or null when it has none or the count is untrustworthy. */
export function detailForCountPhase(phase: GenerationPhase): string | null {
  const copyFor = ownValue(COUNT_DETAIL_COPY, phase.type)
  return copyFor ? copyFor(phase.data) : null
}
